import NiceModal from "@ebay/nice-modal-react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import type { QueryClient } from "@tanstack/react-query";
import { Navigate, Outlet, createRootRouteWithContext } from "@tanstack/react-router";
import { ToastContainer } from "react-toastify";
import { css } from "../../styled-system/css";
import ChatProvider from "../components/chat/ChatProvider";
import AdPlanProvider from "../components/core/blocks/AdPlan/AdPlanProvider";
import GoogleAnalytics from "../components/custom/GoogleAnalytics";
import MouseflowIdentityTracking from "../components/MouseflowIdentityTracking";
import ProfileValidator from "../components/custom/ProfileValidator";
import { DashboardThemeProvider } from "../hooks/useDashboardTheme";
import { FollowPixel } from "../utils/followPixel";
import type { AppStore } from "../redux/store";
import { validateStringSearch } from "../utils/searchParams";

/** Providers that render modals or navigate live inside the router, so they can use its hooks. */
function RootLayout() {
  return (
    <NiceModal.Provider>
      <AdPlanProvider>
        <ChatProvider>
          <DashboardThemeProvider>
            <FollowPixel />
            <ProfileValidator />
            <Outlet />
            <GoogleAnalytics />
            <MouseflowIdentityTracking />
          </DashboardThemeProvider>
          <div className={css({ fontSize: "22px" })}>
            <ReactQueryDevtools initialIsOpen={false} buttonPosition={"bottom-left"} position="right" />
          </div>
        </ChatProvider>
      </AdPlanProvider>
      <ToastContainer className={css({ zIndex: "toast" })} position={"top-right"} />
    </NiceModal.Provider>
  );
}

/** What route guards read: auth and user state from redux, server state from react-query. */
interface RouterContext {
  store: AppStore;
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  validateSearch: validateStringSearch,
  component: RootLayout,
  // Any URL no route claims goes to login, which forwards signed-in users to their dashboard.
  notFoundComponent: () => <Navigate to="/" replace />,
});
